import { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { colors, mediumShadow, spacing } from 'ui/theme';
import { CONTROLBAR_SIZES, CONTROLBAR_VARIANTS } from './constants';
import type { ControlbarSize, ControlbarVariant } from './types';

/** TODO: add component-level design token support */
const Container = styled.div`
  margin: 0;
  padding: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: ${spacing.half};

  &[data-size=${CONTROLBAR_SIZES.SMALL}] {
    padding: ${spacing.threeQuarter};
  }

  &[data-size=${CONTROLBAR_SIZES.MEDIUM}] {
    padding: ${spacing.base};
  }

  &[data-size=${CONTROLBAR_SIZES.LARGE}] {
    padding: ${spacing.plusQuarter};
  }

  &[data-variant=${CONTROLBAR_VARIANTS.PRIMARY}] {
    background-color: ${colors.backgroundPrimary};
    border-radius: ${spacing.threeEights};

    ${mediumShadow}
  }

  &[data-variant=${CONTROLBAR_VARIANTS.SECONDARY}] {
    background-color: ${colors.purple100};
  }

  &[data-variant=${CONTROLBAR_VARIANTS.GHOST}] {
    padding: 0;
    background-color: transparent;
  }

  &[data-variant=${CONTROLBAR_VARIANTS.FLAT}] {
    border-bottom: ${spacing.single} solid ${colors.borderPrimary};
    background-color: ${colors.backgroundPrimary};
  }
`;

type ControlbarProps = PropsWithChildren<{
  /** What is the purpose of the controlbar, e.g. "Controlbar for filtering campaigns" */
  'aria-label': string;
  /** Size of the controlbar */
  size?: ControlbarSize;
  /** Variant of the controlbar */
  variant?: ControlbarVariant;
}>;

export const Controlbar = ({
  children,
  size = CONTROLBAR_SIZES.MEDIUM,
  variant = CONTROLBAR_VARIANTS.PRIMARY,
  'aria-label': ariaLabel = 'Controlbar options',
  ...rest
}: ControlbarProps) => {
  return (
    <Container
      role="toolbar"
      aria-label={ariaLabel}
      data-variant={variant}
      data-size={size}
      {...rest}
    >
      {children}
    </Container>
  );
};
